import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AD_TYPES, GOALS, SB_BRAND_AD_TYPE } from "../../../components/core/blocks/AdPlan/constants";
import { resolveFilterOption } from "../../merchandising/components/tables/resolveFilterOption";

export const servingStatusOptions = [
  { value: "all", label: ($) => $.advertising["All"] },
  { value: "ENDED", label: ($) => $.advertising["Campaign Ended"] },
  { value: "SERVING", label: ($) => $.advertising["Serving"] },
  { value: "REJECTED", label: ($) => $.advertising["Rejected"] },
  { value: "PENDING_REVIEW", label: ($) => $.advertising["Pending Review"] },
  { value: "CAMPAIGN_PAUSED", label: ($) => $.advertising["Campaign Paused"] },
  { value: "INELIGIBLE", label: ($) => $.advertising["Ineligible"] },
  { value: "PORTFOLIO_ENDED", label: ($) => $.advertising["Portfolio Ended"] },
  { value: "CAMPAIGN_INCOMPLETE", label: ($) => $.advertising["Campaign Incomplete"] },
  { value: "CAMPAIGN_OUT_OF_BUDGET", label: ($) => $.advertising["Campaign Out of Budget"] },
  { value: "PORTFOLIO_OUT_OF_BUDGET", label: ($) => $.advertising["Portfolio Out of Budget"] },
  { value: "CAMPAIGN_STATUS_ENABLED", label: ($) => $.advertising["Enabled"] },
  { value: "ADVERTISER_PAYMENT_FAILURE", label: ($) => $.advertising["Advertiser Payment Failure"] },
];

const AD_STATUS_OPTIONS = [
  { value: 1, label: ($) => $.advertising["Active"] },
  { value: 0, label: ($) => $.advertising["Paused"] },
  { value: 2, label: ($) => $.advertising["Pending"] },
  { value: 10, label: ($) => $.advertising["Failed"] },
];

const AD_SOURCE_OPTIONS = [
  { value: "trellis", label: ($) => $.advertising["Trellis"] },
  { value: 25, label: ($) => $.advertising["Imported"] },
];

const CHANNEL_AD_TYPE_OPTIONS = {
  walmart: [
    AD_TYPES.SPONSORED_PRODUCT_WALMART,
    AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART,
    AD_TYPES.SPONSORED_BRAND_WALMART,
    AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART,
  ],
  amazon: [
    AD_TYPES.SPONSORED_PRODUCT,
    AD_TYPES.SPONSORED_BRAND_V2,
    AD_TYPES.SPONSORED_BRAND_VIDEO,
    AD_TYPES.SPONSORED_PRODUCT_AUTO,
    AD_TYPES.SPONSORED_DISPLAY_V2,
    AD_TYPES.CUSTOM,
  ],
  shopify: [],
  google: [],
  target: [],
};

const channel = CHANNEL_AD_TYPE_OPTIONS[localStorage.channel] ? localStorage.channel : "amazon";

const SCHEDULED_ACTION_STATE_OPTIONS = [
  { value: true, label: ($) => $.advertising["With Scheduled Actions"] },
  { value: false, label: ($) => $.advertising["Without Scheduled Actions"] },
];

export const AD_TYPE_FILTER =
  // Ad Type Filter
  {
    name: ($) => $.advertising["Ad Type"],
    id: "ad_type",
    format: "select",
    selectOptions: [
      {
        label: ($) => $.advertising["All Types"],
        value: "all",
      },
      ...Object.values(CHANNEL_AD_TYPE_OPTIONS[channel]),
    ],
    override: true,
    defaultValue: "all",
    operator: "=",
    filterFunction: (ad, filterValue) => {
      if (filterValue !== "all") {
        // Somewhere in processing filterValue coming back as a stringified #, cast compare value
        filterValue = parseInt(filterValue);

        let legacyTypeMatch = false;
        let ignoreBrandVideoAd = false;

        // Handle custom filter based on is_composite flag
        if (filterValue === AD_TYPES.CUSTOM.value && channel === "amazon") {
          return ad?.is_composite;
        }
        if (ad?.ad_type === AD_TYPES.SPONSORED_BRAND.value && filterValue === AD_TYPES.SPONSORED_BRAND_V2.value) {
          legacyTypeMatch = true;
        }
        if (
          ad?.ad_type === AD_TYPES.SPONSORED_BRAND_V2.value &&
          filterValue === AD_TYPES.SPONSORED_BRAND_VIDEO.value &&
          [SB_BRAND_AD_TYPE.VIDEO, SB_BRAND_AD_TYPE.BRAND_VIDEO].includes(ad?.creative?.brand_ad_type)
        ) {
          legacyTypeMatch = true;
        }

        if (
          filterValue === AD_TYPES.SPONSORED_BRAND_V2.value &&
          [SB_BRAND_AD_TYPE.VIDEO, SB_BRAND_AD_TYPE.BRAND_VIDEO].includes(ad?.creative?.brand_ad_type)
        ) {
          ignoreBrandVideoAd = true;
        }

        if (ad?.ad_type === AD_TYPES.SPONSORED_DISPLAY.value && filterValue === AD_TYPES.SPONSORED_DISPLAY_V2.value) {
          legacyTypeMatch = true;
        }

        return (ad?.ad_type === filterValue || legacyTypeMatch) && !ignoreBrandVideoAd;
      }
      return true;
    },
  };

export const AD_STATUS_FILTER = {
  name: ($) => $.advertising["Ad Status"],
  id: "ad_status",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.advertising["All Statuses"],
      value: "all",
      clearable: false,
    },
    ...AD_STATUS_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "=",
  filterFunction: (ad, filterValue) => {
    if (filterValue !== "all") {
      filterValue = parseInt(filterValue);

      // Below handles ad failure display taking precedence over intented status set by user
      const AD_FAILED_STATUS = 10;
      if (filterValue === AD_FAILED_STATUS) {
        return ad.ad_status === AD_FAILED_STATUS;
      }

      return ad.state === filterValue && ad.ad_status !== AD_FAILED_STATUS;
    }
    return true;
  },
};

export const NTM_STATUS_OPTIONS = [
  { value: "unenrolled", label: ($) => $.advertising["Non Managed"] },
  { value: "enrolled", label: ($) => $.advertising["Enrolled"] },
  { value: "accept_bids", label: ($) => $.advertising["Bids Accepted"] },
];

export const NTM_STATUS_FILTER = {
  name: ($) => $.advertising["NTM Ad Status"],
  id: "ntm_ad_status",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.advertising["All Statuses"],
      value: "all",
      clearable: false,
    },
    ...NTM_STATUS_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "=",
  filterFunction: (ad, filterValue) => {
    if (filterValue === "unenrolled") {
      return (
        ad.ad_goal === GOALS.IMPORTED_ADS.value &&
        ad.non_trellis_enrolled_ad === false &&
        ad.non_trellis_enrolled_ad_accept_bids === false
      );
    }
    if (filterValue === "enrolled") {
      return ad.non_trellis_enrolled_ad === true && ad.non_trellis_enrolled_ad_accept_bids === false;
    }
    if (filterValue === "accept_bids") {
      return ad.non_trellis_enrolled_ad_accept_bids === true;
    }
    return true;
  },
};

const AD_NAME_FILTER = {
  name: ($) => $.advertising["Name"],
  id: "ad_name",
  format: "text",
};

export const AD_SOURCE_FILTER = {
  name: ($) => $.advertising["Ad Source"],
  id: "goal",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.advertising["All Sources"],
      value: "all",
      clearable: false,
    },
    ...AD_SOURCE_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "=",
  filterFunction: (ad, filterValue) => {
    if (filterValue !== "all") {
      // Below handles ad failure display taking precedence over intented status set by user
      if (filterValue === GOALS.IMPORTED_ADS.value) {
        return ad.ad_goal === filterValue;
      }

      return ad.ad_goal !== GOALS.IMPORTED_ADS.value;
    }
    return true;
  },
};

export const SCHEDULED_BUDGET_CHANGES_FILTER = {
  name: ($) => $.advertising["Scheduled Actions"],
  id: "scheduled_actions",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.advertising["All Ads"],
      value: "all",
      clearable: false,
    },
    ...SCHEDULED_ACTION_STATE_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "=",
  filterFunction: (ad, filterValue) => {
    if (filterValue !== "all") {
      // Below handles ad failure display taking precedence over intented status set by user
      if (filterValue) {
        return ad.scheduled_actions?.length;
      }

      return ad.scheduled_actions?.length === 0;
    }
    return true;
  },
};

const buildBiddingProgramFilter = (programs) => {
  const programOptions = [...new Set(programs?.filter((program) => program) ?? [])]?.map((program) => ({
    value: program.toLowerCase(),
    label: program,
  }));

  return {
    name: ($) => $.advertising["Bidding Programs"],
    id: "bidding_program_name",
    format: "select",
    selectOptions: [
      {
        value: "all",
        label: ($) => $.advertising["All"],
      },
      {
        value: "withProgram",
        label: ($) => $.advertising["With Bidding Program"],
      },
      {
        value: "withoutProgram",
        label: ($) => $.advertising["Without Bidding Program"],
      },
      ...programOptions.flat(),
    ],
    override: true,
    defaultValue: "all",
    operator: "custom",
    filterFunction: (ad, filterValue) => {
      if (filterValue === "all") {
        return true;
      }

      if (filterValue === "withProgram") {
        return ad.bidding_program_name;
      }

      if (filterValue === "withoutProgram") {
        return !ad.bidding_program_name;
      }

      return ad.bidding_program_name?.toLowerCase() === filterValue?.toLowerCase();
    },
  };
};

const SERVING_STATUS_FILTER = {
  name: ($) => $.advertising["Serving Status"],
  id: "serving_status",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.advertising["All Serving Statuses"],
      value: "all",
    },
    ...servingStatusOptions,
  ],
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (ad, filterValue) => {
    if (filterValue !== "all") {
      return ad.serving_status === filterValue;
    }
    return true;
  },
};

// Resolves the advertising-namespace selectors carried by the filter defs above
// into plain strings, bound to the `advertising` namespace at the call site so
// the generic FilterWidget never has to resolve them. See resolveFilterOption.
export function useAdLifecycleFilters() {
  const { t } = useTranslation(["advertising"]);
  return useMemo(
    () => ({
      AD_TYPE_FILTER: resolveFilterOption(t, AD_TYPE_FILTER),
      AD_STATUS_FILTER: resolveFilterOption(t, AD_STATUS_FILTER),
      NTM_STATUS_FILTER: resolveFilterOption(t, NTM_STATUS_FILTER),
      AD_NAME_FILTER: resolveFilterOption(t, AD_NAME_FILTER),
      AD_SOURCE_FILTER: resolveFilterOption(t, AD_SOURCE_FILTER),
      SCHEDULED_BUDGET_CHANGES_FILTER: resolveFilterOption(t, SCHEDULED_BUDGET_CHANGES_FILTER),
      SERVING_STATUS_FILTER: resolveFilterOption(t, SERVING_STATUS_FILTER),
      buildBiddingProgramFilter: (programs) => resolveFilterOption(t, buildBiddingProgramFilter(programs)),
    }),
    [t],
  );
}
